import React, { useState } from "react";
import moment from "moment-timezone";
import { MySQLAdminData } from "../types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

interface UsersTabProps {
  data: MySQLAdminData;
}

export const UsersTab: React.FC<UsersTabProps> = ({ data }) => {
  const [chartType, setChartType] = useState<"daily" | "monthly">("daily");

  // 차트 데이터 포맷팅
  const formatChartData = (
    rawData: typeof data.dailyTrend | typeof data.monthlyTrend
  ) => {
    return rawData
      .map((item) => {
        // 타입 안전성을 위해 조건부로 속성 접근
        const dateValue =
          chartType === "daily" ? (item as any).date : (item as any).month;

        return {
          ...item,
          formattedDate:
            chartType === "daily"
              ? moment(dateValue).format("MM/DD")
              : moment(dateValue).format("YYYY-MM"),
        };
      })
      .reverse(); // 데이터 순서를 뒤바꿔서 오래된 것부터 최신 순으로
  };

  const chartData = formatChartData(
    chartType === "daily" ? data.dailyTrend : data.monthlyTrend
  );

  const pieData = [
    {
      name: "자체 가입",
      value: parseInt(data.userStats.credentialsUsersRate),
      color: "#06d6a0",
    },
    {
      name: "카카오",
      value: parseInt(data.userStats.kakaoUsersRate),
      color: "#118ab2",
    },
    {
      name: "구글",
      value: parseInt(data.userStats.googleUsersRate),
      color: "#ef476f",
    },
  ];

  return (
    <div className="space-y-8">
      {/* 전체 개요 KPI */}
      <section className="glass-card">
        <h2 className="text-3xl font-bold tracking-tight text-white/90 glow-text mb-6">
          사용자 현황
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="총 가입자"
            value={data.userStats.totalUsers.toLocaleString()}
            accent="blue"
          />
          <KPICard
            title="최근 1일"
            value={data.userStats.newUsers1d.toLocaleString()}
            accent="cyan"
          />
          <KPICard
            title="최근 7일"
            value={data.userStats.newUsers7d.toLocaleString()}
            accent="violet"
          />
          <KPICard
            title="최근 30일"
            value={data.userStats.newUsers30d.toLocaleString()}
            accent="pink"
          />
        </div>
      </section>

      {/* 가입자 추이 차트 */}
      <section className="glass-card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-white/90 glow-text">
            가입자 추이
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setChartType("daily")}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                chartType === "daily"
                  ? "bg-white/20 text-white"
                  : "text-white/70 hover:text-white/90 hover:bg-white/10"
              }`}
            >
              일별
            </button>
            <button
              onClick={() => setChartType("monthly")}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                chartType === "monthly"
                  ? "bg-white/20 text-white"
                  : "text-white/70 hover:text-white/90 hover:bg-white/10"
              }`}
            >
              월별
            </button>
          </div>
        </div>

        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
              />
              <XAxis
                dataKey="formattedDate"
                stroke="rgba(255,255,255,0.7)"
                fontSize={12}
                tick={{ fill: "rgba(255,255,255,0.7)" }}
              />
              <YAxis
                stroke="rgba(255,255,255,0.7)"
                fontSize={12}
                tick={{ fill: "rgba(255,255,255,0.7)" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(0,0,0,0.8)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "8px",
                  color: "white",
                }}
                labelFormatter={(value) =>
                  `${chartType === "daily" ? "날짜" : "월"}: ${value}`
                }
                formatter={(value: number) => [
                  value.toLocaleString(),
                  "가입자 수",
                ]}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#06d6a0"
                strokeWidth={3}
                name="전체 가입자"
                dot={{ fill: "#06d6a0", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: "#06d6a0" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 차트 설명 노티 */}
        <div className="mt-4 text-right">
          <p className="text-white/50 text-xs">
            💡 가입자 수가 0인 날짜는 차트에 표시되지 않습니다.
          </p>
        </div>
      </section>

      {/* 플랫폼별 가입 현황 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            플랫폼별 가입 현황
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(0,0,0,0.8)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    color: "white",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            {pieData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-white/80 text-sm">
                  {item.name} ({item.value}%)
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            플랫폼별 가입자 수
          </h2>
          <div className="space-y-4">
            <PlatformStat
              platform="자체 가입"
              count={data.userStats.credentialsUsers}
              rate={data.userStats.credentialsUsersRate}
              color="from-blue-500 to-cyan-500"
            />
            <PlatformStat
              platform="카카오"
              count={data.userStats.kakaoUsers}
              rate={data.userStats.kakaoUsersRate}
              color="from-yellow-500 to-orange-500"
            />
            <PlatformStat
              platform="구글"
              count={data.userStats.googleUsers}
              rate={data.userStats.googleUsersRate}
              color="from-red-500 to-pink-500"
            />
          </div>
        </section>
      </div>
    </div>
  );
};

function KPICard({
  title,
  value,
  accent = "blue",
}: {
  title: string;
  value: string;
  accent?: "blue" | "cyan" | "violet" | "pink" | "emerald";
}) {
  const accentColors = {
    blue: "from-blue-400/20 to-cyan-400/20 border-blue-400/30 shadow-blue-400/20",
    cyan: "from-cyan-400/20 to-teal-400/20 border-cyan-400/30 shadow-cyan-400/20",
    violet:
      "from-violet-400/20 to-purple-400/20 border-violet-400/30 shadow-violet-400/20",
    pink: "from-pink-400/20 to-rose-400/20 border-pink-400/30 shadow-pink-400/20",
    emerald:
      "from-emerald-400/20 to-green-400/20 border-emerald-400/30 shadow-emerald-400/20",
  };

  return (
    <div className={`glass-kpi ${accentColors[accent]} border border-white/10`}>
      <div className="text-white/70 text-sm tracking-wide font-medium">
        {title}
      </div>
      <div className="text-2xl font-bold mt-2 text-white/95">{value}</div>
    </div>
  );
}

function PlatformStat({
  platform,
  count,
  rate,
  color,
}: {
  platform: string;
  count: number;
  rate: string;
  color: string;
}) {
  return (
    <div className="glass-sub-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/80 font-medium">{platform}</span>
        <span className="text-white/60 text-sm">{rate}%</span>
      </div>
      <div className="flex items-center gap-3">
        <div className={`w-2 h-12 rounded-full bg-gradient-to-b ${color}`} />
        <div>
          <div className="text-2xl font-bold text-white/95">
            {count.toLocaleString()}
          </div>
          <div className="text-white/60 text-sm">명</div>
        </div>
      </div>
    </div>
  );
}
